import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { TargetIcon, SpinnerGapIcon, UsersThreeIcon, ArrowRightIcon, ChartLineUpIcon, SkullIcon } from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchClusters } from "../lib/api";
import { markClustersSeen } from "../lib/useClusterAlerts";
import { QuickBuyLink } from "../components/TradeButton";
import { fmtUsd } from "../lib/format";

const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const timeAgo = (ts) => {
  if (!ts) return "—";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ClusterCard = ({ cl }) => {
  const delta = cl.mcapDeltaPct;
  const up = (delta ?? 0) >= 0;
  const winners = (cl.buyers || []).filter((b) => (b.score || 0) >= 75).length;
  return (
    <div
      data-testid={`cluster-card-${cl.tokenMint}`}
      className="border border-[#A78BFA]/30 bg-[#A78BFA]/[0.05] p-4 sm:p-5 space-y-3 hover:border-[#A78BFA]/60 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center border border-[#A78BFA]/50 text-[#A78BFA] shrink-0">
          <TargetIcon size={20} weight="fill" />
        </div>
        {cl.tokenImage ? (
          <img src={cl.tokenImage} alt="" className="w-10 h-10 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/coin/${cl.tokenMint}`} className="font-heading font-bold text-lg hover:text-neon transition-colors">
              ${cl.tokenSymbol || "token"}
            </Link>
            <span className="flex items-center gap-1 font-mono text-xs px-2 py-0.5 bg-[#A78BFA]/15 border border-[#A78BFA]/50 text-[#A78BFA]">
              <UsersThreeIcon size={13} weight="bold" /> {cl.walletCount} wallets
            </span>
            {cl.proven > 0 && (
              <span data-testid={`cluster-proven-${cl.tokenMint}`} title={`${cl.proven} of ${cl.walletCount} buyers have a proven ≥60 PnL score`}
                className="font-mono text-xs px-2 py-0.5 bg-neon/10 border border-neon/40 text-neon">
                🔥 {cl.proven}/{cl.walletCount} proven
              </span>
            )}
            {winners >= 2 && (
              <span data-testid={`cluster-winners-${cl.tokenMint}`} title={`${winners} recurring winners (PnL score ≥75) bought this together`}
                className="font-mono text-xs px-2 py-0.5 bg-[#F0C000]/15 border border-[#F0C000]/50 text-[#F0C000] font-bold">
                🏆 {winners} winners
              </span>
            )}
            {cl.copyWorthy && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-neon/10 border border-neon/30 text-neon">🔥 COPY</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono">
            First buy {timeAgo(cl.firstTimestamp)} · ~{cl.totalSol} SOL in
          </div>
        </div>
        <div className="shrink-0">
          <QuickBuyLink coin={{ address: cl.tokenMint, url: `https://dexscreener.com/solana/${cl.tokenMint}` }} testid={`cluster-buy-${cl.tokenMint}`} />
        </div>
      </div>

      {/* MCap journey */}
      <div className="flex items-center gap-2 text-sm font-mono bg-black/30 border border-white/5 px-3 py-2.5">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">First seen</div>
          <div className="text-white font-semibold">{cl.firstMarketCap ? fmtUsd(cl.firstMarketCap) : "—"}</div>
        </div>
        <ArrowRightIcon size={16} className="text-muted-foreground mx-1" />
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Now</div>
          <div className="text-white font-semibold">{cl.nowMarketCap ? fmtUsd(cl.nowMarketCap) : "—"}</div>
        </div>
        {delta != null && (
          <span className="ml-auto font-bold text-base" style={{ color: up ? "#00E599" : "#FF3366" }}>
            {up ? "+" : ""}{delta}%
          </span>
        )}
      </div>

      {/* buyers */}
      <div className="flex flex-wrap gap-1.5">
        {(cl.buyers || []).map((b) => {
          const pro = (b.score || 0) >= 60;
          return (
            <span key={b.address} title={b.score ? `PnL score ${b.score}${pro ? " · proven" : ""}` : ""}
              className={`text-[11px] font-mono px-2 py-1 border ${pro ? "bg-neon/10 border-neon/40 text-neon" : "bg-white/5 border-white/10 text-muted-foreground"}`}>
              {pro ? "🔥 " : ""}{b.label || short(b.address)}
            </span>
          );
        })}
      </div>

      <Link to={`/coin/${cl.tokenMint}`} data-testid={`cluster-details-${cl.tokenMint}`} className="flex items-center justify-center gap-2 w-full py-2 text-sm font-semibold border border-white/15 text-muted-foreground hover:text-neon hover:border-neon/40 transition-colors">
        <ChartLineUpIcon size={16} weight="bold" /> Chart & Degen Score
      </Link>
    </div>
  );
};

export default function ClusterBuys() {
  const clientId = getClientId();
  const { data, isLoading } = useQuery({
    queryKey: ["clusters", clientId],
    queryFn: () => fetchClusters(clientId),
    refetchInterval: 30000,
  });
  const clusters = data?.clusters || [];
  const min = data?.min ?? 2;
  const suppressed = data?.suppressed || [];

  useEffect(() => {
    if (data) markClustersSeen(clusters.map((c) => c.tokenMint));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="cluster-buys-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-[#A78BFA] text-black">
            <TargetIcon size={24} weight="fill" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">CLUSTER BUYS</h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Tokens that <span className="text-[#A78BFA] font-semibold">{min}+ of your tracked Smart Money wallets</span> are buying
          within the last 6 hours — the highest-conviction "something big is launching" signal. Sorted by how many wallets have
          piled in. Adjust the threshold on the <Link to="/alerts" className="text-neon hover:underline">Alerts page</Link>.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={32} className="animate-spin text-[#A78BFA]" /></div>
      ) : clusters.length === 0 ? (
        <div className="border border-white/10 bg-card py-20 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <UsersThreeIcon size={30} className="text-muted-foreground" />
          No active clusters right now. When {min}+ of your tracked wallets buy the same token within 6h, it'll show here.
          <Link to="/smart-money" className="text-neon hover:underline">Track more Smart Money wallets →</Link>
        </div>
      ) : (
        <>
          <div className="text-xs font-mono text-muted-foreground">{clusters.length} active convergence{clusters.length === 1 ? "" : "s"} 🎯</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clusters.map((cl) => <ClusterCard key={cl.tokenMint} cl={cl} />)}
          </div>
        </>
      )}

      {suppressed.length > 0 && (
        <div data-testid="clusters-suppressed" className="flex flex-wrap items-center gap-2 border-t border-white/10 pt-4 text-xs font-mono text-muted-foreground">
          <SkullIcon size={14} weight="fill" className="text-danger" />
          {suppressed.length} community-flagged scam{suppressed.length === 1 ? "" : "s"} auto-hidden:
          {suppressed.map((s) => (
            <Link key={s.mint} to={`/coin/${s.mint}`} data-testid={`clusters-suppressed-${s.mint}`}
              className="px-2 py-0.5 bg-danger/10 border border-danger/30 text-danger hover:bg-danger/20 transition-colors">
              ☠️ {s.symbol || "token"} · {s.count}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
